import { createContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getGameDayStatus } from '@/api/auth';
import { useAuth } from '@/hooks/useAuth';
import type { GameDayLock } from '@/types';

export interface GameDayContextValue {
  isLocked: boolean;
  gameDay: GameDayLock | null;
  locks: GameDayLock[];
  isLoading: boolean;
}

export const GameDayContext = createContext<GameDayContextValue | null>(null);

export function GameDayProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, activeSiteId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['game-day-status'],
    queryFn: getGameDayStatus,
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const value = useMemo<GameDayContextValue>(() => {
    const locks = data?.locks ?? [];
    const currentLock = activeSiteId
      ? (locks.find((l) => l.siteId === activeSiteId) ?? locks[0] ?? null)
      : (locks[0] ?? null);
    return {
      isLocked: currentLock?.isLocked ?? false,
      gameDay: currentLock,
      locks,
      isLoading,
    };
  }, [data, activeSiteId, isLoading]);

  return <GameDayContext.Provider value={value}>{children}</GameDayContext.Provider>;
}
